<template>
  <div class="container">
    <div ref="descRef" class="show-more" :style="clampStyle">
      <slot class="content" />
    </div>
    <Button
      v-if="linesCount && linesCount > linesToShow"
      type="link"
      variant="black"
      @click.prevent.stop="toggleViewMore()"
      class="!h-min no-underline relative z-10"
      :class="buttonClass"
      >{{ isExpanded ? 'View more' : 'View less' }}</Button
    >
  </div>
</template>
<script lang="ts" setup>
export interface Props {
  linesToShow?: number;
  buttonClass?: string;
}

const props = withDefaults(defineProps<Props>(), { linesToShow: 12, buttonClass: '' });

import { useResizeObserver } from '@vueuse/core';
import { ref, Ref, watch, onMounted } from 'vue';
import Button from '../button/Button.vue';
import { onUnmounted } from 'vue';

const descRef: Ref<HTMLElement | null> = ref(null);
const linesCount: Ref<number | null> = ref(null);
const isExpanded: Ref<boolean> = ref(false);
const clampStyle: Ref<any> = ref(null);

let lcCountDone = false;
let observerInstance: any;

const observe = () => {
  const { stop } = useResizeObserver(descRef, entries => {
    if (lcCountDone) {
      return;
    }

    lcCountDone = true;

    const el = entries[0].target as HTMLElement;
    const prevLH = el.style.lineHeight;
    const factor = 1000;

    el.style.lineHeight = factor + 'px';
    const height = el.getBoundingClientRect().height;
    el.style.lineHeight = prevLH;

    if (el.style.lineHeight === '') {
      linesCount.value = Math.round(height / factor);
    } else {
      linesCount.value = Math.floor(height / factor);
    }
  });

  return { stop };
};

watch(linesCount, val => {
  if (val && val <= props.linesToShow) {
    isExpanded.value = false;
    return;
  }

  isExpanded.value = true;
  clampStyle.value = {
    '-webkit-line-clamp': props.linesToShow,
    'line-clamp': props.linesToShow,
  };
});

onMounted(() => {
  if (!descRef.value) {
    return;
  }

  observerInstance = observe();
});

onUnmounted(() => observerInstance && observerInstance.stop());

const toggleViewMore = () => {
  isExpanded.value = !isExpanded.value;
  clampStyle.value = !isExpanded.value
    ? null
    : {
        '-webkit-line-clamp': props.linesToShow,
        'line-clamp': props.linesToShow,
      };
};
</script>
<style scoped>
.container {
  @apply flex flex-col;
}

.show-more {
  @apply mb-1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  box-orient: vertical;
  overflow: hidden;
}

.show-more > .content {
  display: inline;
}
.show-more > .content::after {
  content: '\A\A';
  white-space: pre;
}
</style>
